/*🤫*/
/*🤫*/

import {BasketView} from "./BasketView.js";
import {DataContainer, Feature, FeatureStatus} from "@otto-ec/tracking-bct";
import {OcCheckboxV1Events} from "@otto-ec/otto-components/dist-lib/components/checkbox";

export class ServicesServerBoundaryBuilder {

    private isBasket: boolean;

    constructor(isBasket: boolean) {
        this.isBasket = isBasket
    }

    basketUri(resource: string): string {
        const contextResource = this.isBasket ? '/basket' : '/checkout/basket';
        return '/order' + contextResource + resource;
    }

    saveChangedService(basketItemId: string, serviceId: string, state: boolean,
                       successHandler: (response: Response) => Promise<void>,
                       errorHandler: (response: Response) => Promise<void>) {
        const parameters: Record<string, string | number | boolean> = {
            "basketItemId": basketItemId,
            "serviceId": serviceId,
            "selected": state
        };

        const data = window.o_util.core.serialize(parameters)!

        return fetch(this.basketUri("/item/modifyService"), {
            method: 'POST',
            body: data,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        })
            .then(window.o_order?.utils.handleAjax(successHandler, errorHandler))
            .catch(function (error) {
                if (window.o_global.debug.status().activated) {
                    console.error(error);
                }
            });
    }

    saveChangedServiceQuantity(basketItemId: string, serviceId: string, quantity: string,
                               successHandler: (response: Response) => Promise<void>,
                               errorHandler: (response: Response) => Promise<void>): void {
        const parameters: Record<string, string | number | boolean> = {
            "basketItemId": basketItemId,
            "serviceId": serviceId,
            "quantity": quantity
        };

        const data = window.o_util.core.serialize(parameters)!;

        fetch(this.basketUri("/item/modifyServiceQuantity"), {
            method: 'POST',
            body: data,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        })
            .then(window.o_order?.utils.handleAjax(successHandler, errorHandler))
            .catch(function (error) {
                if (window.o_global.debug.status().activated) {
                    console.error(error);
                }
            });
    }

    static initialize(): void {
        const o_order = window.o_order ?? {};
        o_order.services = o_order.services ?? {};

        o_order.services.serverBoundaryBuilder = (isBasket: boolean) => new ServicesServerBoundaryBuilder(isBasket)
    }
}

export class ServicesViewBuilder {
    static readonly basket_selector: string = "order_js_basket";
    static readonly basket_in_checkout_selector: string = 'order_js_checkout_basket';
    static readonly item_selector: string = "order_js_item";
    static readonly itemServiceSelector: string = "order_js_itemService";
    static readonly itemServiceCheckboxSelector: string = "or_js_itemService__checkbox";
    static readonly itemServiceQuantitySelector: string = "order_js_serviceQuantitySelection";
    static readonly itemServiceInfoBtnSelector: string = "or_js_itemService__link";

    private static getServiceData(serviceItem: HTMLElement): ServiceData {
        return {
            id: serviceItem.dataset.serviceid!,
            name: serviceItem.dataset.servicename!,
            price: serviceItem.dataset.serviceprice!,
            quantity: serviceItem.dataset.servicequantity!
        };
    }

    trackActionIncluded(variationId: string, infoButton: string) {
        window.o_order.tracking.submitEvent({
            name: 'click',
            features: [
                {
                    id: 'order_variation_ci',
                    name: 'Variation',
                    status: 'visible',
                    variationId: variationId,
                    labels: {}
                },
            ]
        }, {
            order_InfoButton: [infoButton]
        });
    }

    trackActionSelectable(variationId: string, labels: DataContainer) {
        window.o_order.tracking.submitEvent({
            name: 'click',
            features: [
                {
                    id: 'order_variation_co1',
                    name: 'Variation',
                    status: 'visible',
                    variationId,
                    labels: {}
                }
            ]
        }, labels);
    }

    trackActionSelectableLegacy(variationId: string, status: FeatureStatus, labels: DataContainer) {
        window.o_order.tracking.submitEvent({
            name: 'click',
            features: [
                {
                    id: 'order_variation_co1',
                    name: 'Variation',
                    status: 'visible',
                    variationId,
                    labels: {}
                }, {
                    id: 'order_variation_co2',
                    parentId: 'order_variation_co1',
                    name: 'Service',
                    status,
                    labels
                },
            ]
        }, {
            order_InfoButton: ['service']
        });
    }

    registerServiceOptionSelectionHandler(activateServiceHandler: (a1: string, a2: string, sd: ServiceData, b: boolean) => void,
                                          changeServiceQuantityHandler: (a1: string, a2: string, sd: ServiceData) => void,
                                          basketSelector: string): void {
        const basket = document.getElementsByClassName(basketSelector)[0] as HTMLElement;
        const isBasketPage = basketSelector === ServicesViewBuilder.basket_selector;
        if (basket) {
            window.o_util.event.delegate(basket, "oc-property-change", "." + ServicesViewBuilder.itemServiceCheckboxSelector, function (event: OcCheckboxV1Events["oc-property-change"]) {
                const isChecked = event.detail.checked.current;
                const basketItemId = ServicesViewBuilder.readBasketItemIdFor(event.target as Element, isBasketPage)!;
                const variationId = ServicesViewBuilder.readVariationIdFor(event.target as Element, isBasketPage)!;
                const serviceItem = window.o_util.dom.getParentByClassName(event.target as Element, ServicesViewBuilder.itemServiceSelector) as HTMLElement;
                const serviceData = ServicesViewBuilder.getServiceData(serviceItem);
                activateServiceHandler(basketItemId, variationId, serviceData, isChecked);
            });

            window.o_util.event.delegate(basket, "change", "." + ServicesViewBuilder.itemServiceQuantitySelector, function () {
                const variationId = ServicesViewBuilder.readVariationIdFor(this, isBasketPage)!;
                const basketItemId = ServicesViewBuilder.readBasketItemIdFor(this, isBasketPage)!;
                const serviceItem = window.o_util.dom.getParentByClassName(this, ServicesViewBuilder.itemServiceSelector) as HTMLElement;
                const serviceData = ServicesViewBuilder.getServiceData(serviceItem);
                const inputElement = this as HTMLInputElement
                serviceData.quantity = inputElement.value;

                changeServiceQuantityHandler(basketItemId, variationId, serviceData);
            });

            window.o_util.event.delegate(basket, 'click', '.' + ServicesViewBuilder.itemServiceInfoBtnSelector, (event: MouseEvent) => {
                /*🤫*/
                const itemService = window.o_util.dom.getParentByClassName<HTMLElement>(event.target as Element, 'order_js_itemService');
                const variationId = window.o_util.dom.getParentByClassName(event.target as Element, 'order_js_item')?.getAttribute('data-variation');

                const serviceId = itemService?.getAttribute('data-serviceid') ?? '';
                const serviceIdToTrackingValue: Record<string, string> = {
                    RETURN_PICKUPSERVICE: 'ReturnGuarantee',
                    HOME_DELIVERY: 'PlaceOfUse'
                };

                if (serviceIdToTrackingValue.hasOwnProperty(serviceId)) {
                    this.trackActionIncluded(variationId!, serviceIdToTrackingValue[serviceId]);

                } else if (itemService?.dataset.servicetype === 'service_type_orderable') {
                    this.trackActionSelectable(variationId!, {
                        order_Service: [itemService.dataset.servicename!],
                        order_ServiceActivity: ['details'],
                    });
                }
            });
        }

        /*🤫*/
        window.o_util.event.delegate(basket, 'click', '.or_js_basketItem__curbDeliveryInfo', (event: MouseEvent) => {
            const variationId = window.o_util.dom.getParentByClassName(event.target as Element, 'order_js_item')?.getAttribute('data-variation')
            this.trackActionIncluded(variationId!, 'Curbside');
        });
    }

    /*🤫*/


    private static readBasketItemIdFor(elem: Element, isBasketPage: boolean) {
        if (isBasketPage) {
            const parent = window.o_util.dom.getParentByClassName(elem, ServicesViewBuilder.item_selector);
            const basketItemChild = parent?.getElementsByClassName("order_js_basketItemId")[0] as HTMLInputElement | null;

            return basketItemChild ? basketItemChild.value : "";
        }

        return ServicesViewBuilder.readBasketItemIdInCheckoutFor(elem);
    }

    private static readVariationIdFor(elem: Element, isBasketPage: boolean) {
        if (isBasketPage) {
            const parent = window.o_util.dom.getParentByClassName(elem, ServicesViewBuilder.item_selector) as HTMLInputElement | null;
            const basketItemVariationId = parent?.getElementsByClassName("order_js_variationId")[0] as HTMLInputElement | undefined;

            return basketItemVariationId ? basketItemVariationId.value : "";
        }

        return ServicesViewBuilder.readVariationIdInCheckoutFor(elem);
    }

    /*🤫*/


    registerToggleServicesLinkHandler(): void {

        const basket = document.getElementsByClassName(ServicesViewBuilder.basket_in_checkout_selector)[0] as HTMLElement;

        if (basket) {
            window.o_util.event.delegate(basket, 'click', '.or_js_toggleSelectableServices', function () {
                const basketItemId = ServicesViewBuilder.readBasketItemIdInCheckoutFor(this) ?? '';
                const isServiceSectionExpandedAfterToggling = ServicesViewBuilder.toggleServiceSection(basketItemId);

                const getStatusOfServices = (service: HTMLElement) => {
                    if (service.dataset.mandatory === 'true') {
                        return 'mandatory';
                    } else if (service.dataset.preselected === 'true') {
                        return 'preselected';
                    }

                    const checkbox = service.getElementsByClassName(ServicesViewBuilder.itemServiceCheckboxSelector)[0] as HTMLOcCheckboxV1Element;

                    return checkbox?.checked ? 'selected' : 'deselected';
                }

                const getServicesData = (): Feature[] => {
                    const services = this.nextElementSibling?.getElementsByClassName('order_js_itemService') as HTMLCollectionOf<HTMLElement> | undefined;

                    return !services ? [] : Array.from(services).map((service, index): Feature => {
                        return {
                            id: 'order_service_' + (index + 1),
                            parentId: 'order_variation',
                            name: 'Service',
                            status: getStatusOfServices(service),
                            labels: {
                                order_Service: [service.dataset.servicename!],
                                order_ServicePrice: [service.dataset.serviceprice!],
                                order_ServiceQuantity: [service.dataset.servicequantity!],
                                order_ServiceId: [service.dataset.serviceid!],
                                order_ServiceCharge: [parseFloat(service.dataset.serviceprice!) <= 0 ? 'free' : 'charge'],
                                order_ServiceStatus: [getStatusOfServices(service) === 'deselected' ? 'deselected' : 'selected'],
                                order_ServiceSum: [(parseFloat(service.dataset.serviceprice!) * parseInt(service.dataset.servicequantity!, 10)).toFixed(2)]
                            }
                        }
                    });
                }

                window.o_order.tracking.submitEvent({
                    name: isServiceSectionExpandedAfterToggling ? 'expand' : 'collapse',
                    features: [
                        {
                            id: 'order_variation',
                            variationId: ServicesViewBuilder.readVariationIdInCheckoutFor(this)!,
                            name: 'Variation',
                            status: 'visible',
                            labels: {}
                        },
                        ...getServicesData()
                    ]
                });
            });
        }
    }

    uncollapseServiceSectionIfErrorExists(): void {
        [].forEach.call(document.getElementsByClassName("or_js_itemService__error"), (el: Element) => {
            ServicesViewBuilder.toggleServiceSection(ServicesViewBuilder.readBasketItemIdInCheckoutFor(el)!);
        });
    }

    registerClickTracking() {
        const dataTrackingName = 'data-tracking-name';
        const trackingElements = Array.from(document.querySelectorAll<HTMLElement>(`[${dataTrackingName}]`));

        trackingElements.forEach(element => {
            element.addEventListener('click', () => {
                const dataset = element.dataset;
                const trackingMethod = dataset.trackingMethod ? dataset.trackingMethod : 'submitEvent';

                /*🤫*/
                /*🤫*/
                window.o_order.tracking[trackingMethod]({name: 'click', features: []}, {
                    order_InfoButton: dataset.trackingValue
                });
            });
        });
    }

    static readBasketItemIdInCheckoutFor(elem: Element) {
        return window.o_util.dom.getParentByClassName(elem, ServicesViewBuilder.item_selector)?.getAttribute('data-basketitemid');
    }


    static readVariationIdInCheckoutFor(elem: Element) {
        return window.o_util.dom.getParentByClassName(elem, ServicesViewBuilder.item_selector)?.getAttribute('data-variation');
    }


    static getCheckoutItemByBasketItemId(basketItemId: string, basketFromRequest: Element | undefined = undefined) {
        const basket = basketFromRequest || document.getElementsByClassName(ServicesViewBuilder.basket_in_checkout_selector)[0];
        return basket ? basket.querySelector("." + ServicesViewBuilder.item_selector + "[data-basketitemid='" + basketItemId + "']") : undefined;
    }


    static toggleServiceSection(basketItemId: string, basketFromRequest: Element | undefined = undefined): boolean {
        const checkoutItem = ServicesViewBuilder.getCheckoutItemByBasketItemId(basketItemId, basketFromRequest);
        let isServiceSectionExpandedAfterToggling = false;

        if (checkoutItem) {
            const serviceContainer = checkoutItem.querySelector(".or_js_includedServices")!;
            const originalServiceContainer = ServicesViewBuilder.getCheckoutItemByBasketItemId(basketItemId)?.querySelector(".or_js_includedServices");
            const headlineServiceContainer = serviceContainer.querySelector(".or_js_headlineServices");
            const adjustServicesContainer = serviceContainer.querySelector<HTMLElement>(".or_js_adjustServices")!;
            const openClass: string = "or_includedServices--open";
            const serviceIconClass: string = ".or_js_serviceIcon";
            const isServiceSectionExpandedBeforeToggling = originalServiceContainer?.classList.contains(openClass) || false;

            const addTransitionDelay = (forwards: boolean) => {
                return (icon: HTMLElement, index: number, icons: HTMLElement[]) => {
                    const delayStep = 0.05;
                    const widthDelayAdjustment = 0.2;
                    const delayFactor = forwards ? index : icons.length - 1 - index;
                    const delay = delayFactor * delayStep;
                    const scaleDelay = forwards ? delay + widthDelayAdjustment : delay;
                    const widthDelay = forwards ? delay : delay + widthDelayAdjustment; /*🤫*/
                    icon.style.transitionDelay = scaleDelay + 's, ' + widthDelay + 's, ' + widthDelay + 's';
                }
            }

            if (adjustServicesContainer) {
                adjustServicesContainer.style.transitionDelay = isServiceSectionExpandedBeforeToggling ? '0.5s' : '0s';
            }
            [].forEach.call(headlineServiceContainer?.querySelectorAll(serviceIconClass), addTransitionDelay(isServiceSectionExpandedBeforeToggling));
            [].forEach.call(adjustServicesContainer?.querySelectorAll(serviceIconClass), addTransitionDelay(!isServiceSectionExpandedBeforeToggling));
            [].forEach.call(adjustServicesContainer?.querySelectorAll(".or_itemService__selected"), addTransitionDelay(!isServiceSectionExpandedBeforeToggling));

            let height: string = "0";
            if (basketFromRequest) {
                height = 'auto'; /*🤫*/
            } else if (!isServiceSectionExpandedBeforeToggling) {
                height = getComputedStyle(adjustServicesContainer.querySelector(".or_js_adjustServicesTable")!).height;
            }

            if (adjustServicesContainer) {
                adjustServicesContainer.style.height = height;
            }

            if (serviceContainer) {
                serviceContainer.classList.toggle(openClass);
            }

            isServiceSectionExpandedAfterToggling = !isServiceSectionExpandedBeforeToggling;
        }
        return isServiceSectionExpandedAfterToggling;
    }

    static updateServiceElementHeightToRealHeight(basketItemId: string): void {
        const adjustServicesElement = ServicesViewBuilder.getCheckoutItemByBasketItemId(basketItemId)?.querySelector<HTMLElement>('.or_js_adjustServices');
        if (adjustServicesElement) {
            setTimeout(() => {
                adjustServicesElement.style.height = getComputedStyle(adjustServicesElement).height;
            }, 1)
        }
    }

    handleServiceChangeInCheckout(basketItemId: string, basketView: BasketView, data: string) {
        const container = document.createElement('div');
        container.innerHTML = data;

        ServicesViewBuilder.toggleServiceSection(basketItemId, container);

        basketView.updateBasket(container.innerHTML);
        /*🤫*/
        /*🤫*/
        ServicesViewBuilder.updateServiceElementHeightToRealHeight(basketItemId);
    }

    static initialize(): void {
        const o_order = window.o_order ?? {};
        o_order.services = o_order.services ?? {};
        o_order.services.viewBuilder = () => new ServicesViewBuilder()
    }
}

export class ServicesPresenterBuilder {

    view: ServicesViewBuilder;
    parentView: BasketView;
    serverBuilder: (b: boolean) => ServicesServerBoundaryBuilder;
    context: string;
    server: ServicesServerBoundaryBuilder;

    constructor(view: ServicesViewBuilder, parentView: BasketView, serverBuilder: (b: boolean) => ServicesServerBoundaryBuilder, context: string) {
        this.view = view;
        this.parentView = parentView;
        this.serverBuilder = serverBuilder;
        this.context = context;
        this.server = serverBuilder(this.isBasket());
    }

    isBasket(): boolean {
        return this.context === window.o_order.globals.CONTEXT.BASKET;
    }

    getTrackingLabels(serviceData: ServiceData, selected: string): Record<string, string[]> {
        return {
            order_Service: [serviceData.name],
            order_ServicePrice: [serviceData.price],
            order_ServiceQuantity: [serviceData.quantity],
            order_ServiceId: [serviceData.id],
            order_ServiceCharge: serviceData.price === '0.00' ? ['free'] : ['charge'],
            order_ServiceStatus: [selected]
        };
    }

    createServiceChangedTrackingHandler(variationId: string, serviceData: ServiceData, state: boolean) {
        return () => {
            const name = state === true ? "select" : "deselect";
            const selected = state === true ? "selected" : "deselected";
            const featureName = state === true ? "ServiceSelected" : "ServiceDeselected";
            window.o_order.tracking.submitEventMerge({
                name: name,
                features: [
                    {id: 'order_service_sel', name: featureName, status: selected, labels: {}},
                    {
                        id: 'order_service_sel_1',
                        variationId: variationId,
                        name: 'Variation',
                        parentId: 'order_service_sel',
                        status: 'visible',
                        labels: {}
                    },
                    {
                        id: 'order_service_sel_2',
                        name: 'Service',
                        parentId: 'order_service_sel_1',
                        status: selected,
                        labels: this.getTrackingLabels(serviceData, selected)
                    }
                ]
            });
        }
    }

    createServiceChangedQuantityTrackingHandler(variationId: string, serviceData: ServiceData) {
        return (generateId: boolean) => {
            window.o_order.tracking.submitEventMerge({
                name: 'change',
                features: [
                    {id: 'order_service_ch_q', name: 'ServiceQuantityChanged', status: 'changed', labels: {}},
                    {
                        id: 'order_service_ch_q_1',
                        variationId: variationId,
                        name: 'Variation',
                        parentId: 'order_service_ch_q',
                        status: 'visible',
                        labels: {}
                    },
                    {
                        id: 'order_service_ch_q_2',
                        name: 'Service',
                        parentId: 'order_service_ch_q_1',
                        status: 'changed',
                        labels: this.getTrackingLabels(serviceData, 'selected')
                    }
                ]
            }, generateId);
        }
    }

    changeService = (basketItemId: string, variationId: string, serviceData: ServiceData, state: boolean) => {
        return this.server.saveChangedService(basketItemId, serviceData.id, state,
            this.createSaveChangedServiceSuccessCallback(basketItemId, this.createServiceChangedTrackingHandler(variationId, serviceData, state)),
            this.createSaveChangedServiceErrorCallback());
    }

    createSaveChangedServiceSuccessCallback(basketItemId: string, trackingHandler: (b: boolean) => void) {
        return async (response: Response) => {
            const redirectUrl = window.o_order.utils.readResponseRedirectTo(response);
            if (redirectUrl) {
                trackingHandler(true);
                window.o_order.utils.goto(window, redirectUrl);
            } else {
                const responseText = await response.text();
                if (this.isBasket()) {
                    this.parentView.updateBasket(responseText);
                } else {
                    this.view.handleServiceChangeInCheckout(basketItemId, this.parentView, responseText);
                }
                trackingHandler(false);
            }
        };
    }

    createSaveChangedServiceErrorCallback(): (response: Response) => Promise<void> {
        return async (response: Response) => {
            /*🤫*/
            /*🤫*/
            const responseText = await response.text();
            this.parentView.updateBasket(responseText);
        };
    }

    changeServiceQuantity(basketItemId: string, variationId: string, serviceData: ServiceData) {
        this.server.saveChangedServiceQuantity(basketItemId, serviceData.id, serviceData.quantity,
            this.createSaveChangedServiceSuccessCallback(basketItemId, this.createServiceChangedQuantityTrackingHandler(variationId, serviceData)),
            this.createSaveChangedServiceErrorCallback());
    }

    init() {
        this.view.registerClickTracking();

        if (this.isBasket()) {
            this.view.registerServiceOptionSelectionHandler(
                (basketItemId: string, variationId: string, serviceData: ServiceData, state: boolean) => {
                    this.changeService(basketItemId, variationId, serviceData, state);
                },
                (basketItemId: string, variationId: string, serviceData: ServiceData) => {
                    this.changeServiceQuantity(basketItemId, variationId, serviceData)
                },
                "order_js_basket"
            );
        } else {
            this.view.registerServiceOptionSelectionHandler(
                (basketItemId: string, variationId: string, serviceData: ServiceData, state: boolean) => {
                    this.changeService(basketItemId, variationId, serviceData, state);
                },
                (basketItemId: string, variationId: string, serviceData: ServiceData) => {
                    this.changeServiceQuantity(basketItemId, variationId, serviceData)
                },
                "order_js_checkout_basket"
            );
            this.view.registerToggleServicesLinkHandler();
            this.view.uncollapseServiceSectionIfErrorExists();
        }
    }

    static initialize() {
        const o_order = window.o_order ?? {};
        o_order.services = o_order.services ?? {};
        o_order.services.presenterBuilder = (view: ServicesViewBuilder, parentView: BasketView, serverBuilder: (b: boolean) => ServicesServerBoundaryBuilder, context: string) => new ServicesPresenterBuilder(view, parentView, serverBuilder, context)
    }
}

interface ServiceData {
    name: string;
    price: string;
    quantity: string;
    id: string;
}
